/**
 * <LiveTwinStrip> — mes autres appareils connectés au live, collés en haut de ma vignette (molecule,
 * `proposé`).
 *
 * On reste connecté dans un navigateur sans s'en rendre compte (Wajih, daily du 21/10 : « il me paraît
 * essentiel que l'utilisateur qui est connecté en multi-device puisse voir qu'il est connecté en
 * multi-device »). Ma vignette (« Toi ») reste l'appareil où je suis ; mes autres appareils se montrent
 * sur une bande collée en haut d'elle, de sa largeur, séparée d'elle par un filet. Plusieurs : la bande
 * se partage entre eux, trois cases au plus, la dernière dit combien il en reste (« +2 »). Chaque case
 * montre ce que l'appareil envoie : l'écran qu'il partage, sinon sa caméra (`renderVideo`, que l'écran
 * remplit) ; sans piste, mon visage. Une marque en coin (deux feuilles l'une sur l'autre) dit que c'est
 * un double de moi. Rien ne se coupe en deux et rien ne s'ajoute à ma vignette : le partage de
 * l'appareil où je suis ne la change pas.
 *
 * Elle ne se place pas elle-même : la scène la pose au-dessus de ma vignette (`stageLayout.twins`).
 *
 * Le fond est la surface nuit (`surface-night`) dans les deux thèmes, comme `<VideoTile>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type UILiveParticipant, type UILiveTwin } from "../../../ports/primary/ui/types/studentLive"
import { TWIN_LABEL } from "./liveTwinStripGeometry"

export type { UILiveTwin }

export type LiveTwinStripProps = {
    /** Mes autres appareils, dans l'ordre de la salle. Vide : rien. */
    twins: UILiveTwin[]
    /** Moi : le visage d'une case sans piste. */
    me: UILiveParticipant
    width: number
    /** Toute sa hauteur : la ligne « Aussi connecté » (`TWIN_LABEL`), puis les cases. */
    height: number
    /** La piste d'un appareil (l'écran qu'il partage, sinon sa caméra) ; `null` : mon visage. */
    renderVideo?: (twinId: string) => React.ReactNode
    testID?: string
}

/** Au-delà, la dernière case compte ce qui reste. */
const MAX_CELLS = 3

const TEXTS = {
    caption: "Aussi connecté",
    label: (count: number) =>
        count === 1
            ? "Tu es aussi connecté sur un autre appareil"
            : `Tu es aussi connecté sur ${count} autres appareils`,
    more: (count: number) => `+${count}`,
}

export function LiveTwinStrip(props: LiveTwinStripProps) {
    const { twins, me, width, height, renderVideo, testID } = props
    if (twins.length === 0) return null
    const shown = twins.length > MAX_CELLS ? twins.slice(0, MAX_CELLS - 1) : twins
    const rest = twins.length - shown.length

    return (
        <View
            style={styles.strip(width, height)}
            testID={testID}
            accessible
            accessibilityRole="image"
            accessibilityLabel={TEXTS.label(twins.length)}
        >
            <View style={styles.caption} pointerEvents="none">
                <View style={styles.captionChip}>
                    <Text size="text-xs" weight="semibold" color="white" numberOfLines={1}>
                        {TEXTS.caption}
                    </Text>
                </View>
            </View>
            <View style={styles.cells}>
                {shown.map((twin) => {
                    const video = renderVideo?.(twin.id) ?? null
                    return (
                        <View key={twin.id} style={styles.cell}>
                            {video != null ? (
                                <View style={styles.fill}>{video}</View>
                            ) : (
                                <Avatar uri={me.avatarUrl} name={me.name} size={20} />
                            )}
                        </View>
                    )
                })}
                {rest > 0 && (
                    <View style={styles.cell}>
                        <Text size="text-xs" weight="semibold" color="white">
                            {TEXTS.more(rest)}
                        </Text>
                    </View>
                )}
                <View style={styles.mark} pointerEvents="none">
                    <Icon name="copy" size={10} color="white" />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    strip: (width: number, height: number) => ({ width, height }),
    /** La ligne du mot, au-dessus des cases : sur la vidéo du masteur, l'aplat des repères le porte. */
    caption: { height: TWIN_LABEL, alignItems: "flex-start", justifyContent: "flex-start" },
    captionChip: {
        paddingHorizontal: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colors.backdrop,
    },
    cells: {
        flex: 1,
        minHeight: 0,
        flexDirection: "row" as const,
        gap: 1,
        overflow: "hidden" as const,
        borderTopLeftRadius: theme.radius.md,
        borderTopRightRadius: theme.radius.md,
        borderBottomLeftRadius: theme.radius.sm,
        borderBottomRightRadius: theme.radius.sm,
        // Entre deux cases, un filet clair : deux appareils, deux images.
        backgroundColor: theme.colors["border-subtle"],
    },
    cell: {
        flex: 1,
        minWidth: 0,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        backgroundColor: theme.colors["surface-night"],
    },
    fill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%" },
    /** La marque du double, au coin haut gauche, sur l'aplat des repères de la vidéo. */
    mark: {
        position: "absolute",
        top: theme.spacing["1"],
        left: theme.spacing["1"],
        padding: 3,
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colors.backdrop,
    },
}))
